<script setup lang="ts">
  import { computed, ref, type PropType } from 'vue'
  import { XIcon, HistoryIcon, RotateCcw, Check } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { TooltipProvider, TooltipTrigger, TooltipContent, Tooltip } from '@/components/ui/tooltip'
  import PDFViewer from '@/components/pdf/pdfViewer.vue'
  import DatamapperSource from '@/components/app/cases/DatamapperSource.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import {
    type CustomerEntityChange as EntityChange,
    type CustomerCaseDataChange
  } from '@/types/generated'

  const props = defineProps({
    changes: {
      type: Object as PropType<EntityChange[] | CustomerCaseDataChange[]>,
      required: true
    },
    latestChange: {
      type: Object as PropType<EntityChange | CustomerCaseDataChange | null>,
      required: false,
      default: null
    },
    showTimeTravel: {
      type: Boolean,
      required: true
    }
  })

  const emit = defineEmits(['onVersionSelect'])

  const { t } = useTranslation({
    en: {
      page: 'Page',
      today: 'Today',
      yesterday: 'Yesterday',
      versionHistory: 'Version History',
      showMore: 'Show more',
      showLess: 'Show less',
      document: 'Document',
      chatbot: 'Chatbot',
      email: 'E-Mail',
      contextualAnalysis: 'Contextual Analysis',
      phonebot: 'Phonebot',
      manualChange: 'Manual Change',
      ai: 'AI',
      restore: 'Restore',
      deletedDocument: '[DELETED]'
    },
    de: {
      page: 'Seite',
      today: 'Heute',
      yesterday: 'Gestern',
      versionHistory: 'Versionsverlauf',
      showMore: 'Mehr anzeigen',
      showLess: 'Weniger anzeigen',
      document: 'Dokument',
      chatbot: 'Chatbot',
      email: 'E-Mail',
      contextualAnalysis: 'Kontextanalyse',
      phonebot: 'Phonebot',
      manualChange: 'Manuelle Änderung',
      ai: 'KI',
      restore: 'Wiederherstellen',
      deletedDocument: '[GELÖSCHT]'
    }
  })

  const isPopoverOpen = ref(false)
  const expandedChanges = ref(new Set<string>())
  const isPdfOpen = ref(false)
  const pdfUrl = ref('')
  const selectedChangeForPdf = ref<EntityChange | CustomerCaseDataChange | null>(null)

  const toggleExpand = (uuid: string) => {
    const newSet = new Set(expandedChanges.value)
    if (newSet.has(uuid)) {
      newSet.delete(uuid)
    } else {
      newSet.add(uuid)
    }
    expandedChanges.value = newSet
  }

  const openPdf = (change: EntityChange | CustomerCaseDataChange) => {
    const document = change.ai_augmentation?.document
    if (document) {
      selectedChangeForPdf.value = change
      pdfUrl.value = document.url
      isPdfOpen.value = true
      isPopoverOpen.value = false
    }
  }

  const handleOpenPdf = (change: EntityChange | CustomerCaseDataChange) => {
    openPdf(change)
  }

  const getInitialPage = (change: EntityChange | CustomerCaseDataChange) => {
    const pages = change.ai_augmentation?.referenced_document_pages
    if (pages && pages.length > 0) {
      return pages[0]
    }
    return 1
  }

  const isDocumentDeleted = (change: EntityChange | CustomerCaseDataChange) => {
    return change.ai_augmentation?.document?.is_deleted
  }

  const shortenDocumentTitle = (title: string) => {
    if (title.length > 20) {
      return title.slice(0, 12) + '[…].pdf'
    }
    return title
  }

  const getDocumentDisplayTitle = (change: EntityChange | CustomerCaseDataChange) => {
    const document = change.ai_augmentation?.document
    if (document?.filename) {
      return shortenDocumentTitle(document.filename)
    }
    if (document?.title) {
      return shortenDocumentTitle(document.title)
    }
    return null
  }

  const getDeletedDocumentDisplayTitle = (change: EntityChange | CustomerCaseDataChange) => {
    if (isDocumentDeleted(change)) {
      return t('deletedDocument') + ' ' + getDocumentDisplayTitle(change)
    }
    return null
  }

  const isTextTooLong = (text?: string) => {
    return text && text.length > 50
  }

  const isExpanded = (uuid: string) => {
    return expandedChanges.value.has(uuid)
  }

  const orderedChanges = computed(() => {
    return [...props.changes].sort((a, b) => {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    })
  })

  const selectedVersion = computed(() => orderedChanges.value[0]?.uuid || '')

  const handleVersionSelect = async (uuid: string) => {
    const selectedChange = orderedChanges.value.find((change) => change.uuid === uuid)
    if (selectedChange && selectedChange.uuid !== orderedChanges.value[0]?.uuid) {
      emit('onVersionSelect', selectedChange.new_value)
    }
  }

  const getLatestChangeDate = () => {
    if (!props.latestChange) return ''
    const currentDate = new Date()
    const latestChangeDate = new Date(props.latestChange.created_at)
    if (latestChangeDate.toDateString() === currentDate.toDateString()) {
      return t('today')
    } else if (
      latestChangeDate.toDateString() ===
      new Date(currentDate.getTime() - 24 * 60 * 60 * 1000).toDateString()
    ) {
      return t('yesterday')
    }

    return new Date(props.latestChange.created_at).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }
</script>

<template>
  <div class="flex items-center justify-between">
    <span
      v-if="latestChange"
      class="text-sm text-neutral-800"
    >
      {{ getLatestChangeDate() }}
    </span>
    <Popover v-model:open="isPopoverOpen">
      <PopoverTrigger>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Button
                v-if="latestChange && showTimeTravel"
                variant="secondary"
                class="h-8 w-12 p-1.5"
              >
                <div class="flex items-center gap-1 text-gray-500">
                  <HistoryIcon class="h-5 w-5" />
                  <span class="text-sm">{{ changes.length }}</span>
                </div>
              </Button>
            </TooltipTrigger>
            <TooltipContent
              variant="secondary"
              show-arrow
            >
              {{ t('versionHistory') }}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </PopoverTrigger>
      <PopoverContent class="w-[100%] md:w-[650px]">
        <div>
          <div
            class="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-3 py-2"
          >
            <h4 class="text-sm font-medium leading-none">{{ t('versionHistory') }}</h4>
            <Button
              variant="ghost"
              size="icon"
              class="h-4 w-4"
              @click="isPopoverOpen = false"
            >
              <XIcon class="h-4 w-4" />
            </Button>
          </div>
          <div class="max-h-[300px] overflow-y-auto p-1">
            <div class="space-y-2">
              <div
                v-for="change in orderedChanges"
                :key="change.uuid"
                class="flex items-center gap-2 rounded-lg border p-2 transition-colors"
                :class="{
                  'border-2 border-purple-600 bg-purple-100': selectedVersion === change.uuid,
                  'border-gray-200': selectedVersion !== change.uuid
                }"
              >
                <div class="min-w-0 flex-1 px-2">
                  <div
                    class="truncate text-sm font-medium"
                    :class="{
                      'line-clamp-1': !isExpanded(change.uuid) && isTextTooLong(change.new_value)
                    }"
                  >
                    {{ change.new_value }}
                  </div>
                  <button
                    v-if="isTextTooLong(change.new_value)"
                    class="text-xs text-purple-700 hover:underline"
                    @click.stop="toggleExpand(change.uuid)"
                  >
                    {{ isExpanded(change.uuid) ? t('showLess') : t('showMore') }}
                  </button>
                </div>
                <div class="flex items-center gap-2 px-2">
                  <DatamapperSource
                    :change="change"
                    :disable-pdf-viewer="true"
                    @open-pdf="handleOpenPdf"
                  />
                </div>
                <div class="flex items-center gap-2 px-2">
                  <span class="w-[100px] whitespace-nowrap text-sm text-muted-foreground">
                    {{
                      new Date(change.created_at).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })
                    }}
                  </span>
                  <Button
                    v-if="selectedVersion === change.uuid"
                    variant="ghost"
                    size="icon"
                    class="h-8 w-8 cursor-default hover:bg-purple-100"
                  >
                    <Check class="h-4 w-4 text-purple-600" />
                  </Button>
                  <TooltipProvider v-else>
                    <Tooltip>
                      <TooltipTrigger as-child>
                        <Button
                          variant="secondary"
                          size="icon"
                          class="h-8 w-8"
                          @click="handleVersionSelect(change.uuid || '')"
                        >
                          <RotateCcw class="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent
                        variant="secondary"
                        show-arrow
                      >
                        {{ t('restore') }}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  </div>

  <PDFViewer
    v-if="selectedChangeForPdf?.ai_augmentation?.document"
    v-model:isOpen="isPdfOpen"
    v-model:url="pdfUrl"
    :filename="selectedChangeForPdf?.ai_augmentation?.document?.filename"
    :initial-page="getInitialPage(selectedChangeForPdf)"
    :scale="1.0"
    :document="selectedChangeForPdf?.ai_augmentation?.document"
  />
</template>
